import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { MessageCircle, X } from 'lucide-react';
import { toast } from 'sonner';
import { ConversationList } from './chat/ConversationList';
import { ChatView } from './chat/ChatView';
import { NewConversation } from './chat/NewConversation';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState('list');
  const [conversations, setConversations] = useState([]);
  const [activeConv, setActiveConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [memberCount, setMemberCount] = useState(0);
  const [msgText, setMsgText] = useState('');
  const [allUsers, setAllUsers] = useState([]);
  const [totalUnread, setTotalUnread] = useState(0);
  const [searchUser, setSearchUser] = useState('');
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupMembers, setNewGroupMembers] = useState([]);
  const [newConvType, setNewConvType] = useState('dm');
  const [sending, setSending] = useState(false);
  const [typingUsers, setTypingUsers] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const [showReactionPicker, setShowReactionPicker] = useState(null);
  const [toviConv, setToviConv] = useState(null);
  const [toviTyping, setToviTyping] = useState(false);
  const pollRef = useRef(null);
  const typingPollRef = useRef(null);
  const typingTimerRef = useRef(null);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  const fetchTovi = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/chat/tovi`, { headers });
      setToviConv(res.data);
    } catch { /* silent */ }
  }, []);

  const checkToviNotifications = useCallback(async () => {
    try {
      const res = await axios.post(`${API}/chat/tovi/check-notifications`, {}, { headers });
      if (res.data.notifications_pushed > 0) {
        // Refresh Tovi conv to show updated last_message and unread count
        fetchTovi();
      }
    } catch { /* silent */ }
  }, []);

  const fetchConversations = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/chat/conversations`, { headers });
      // Filter out tovi-type conversations from the regular list
      setConversations(res.data.filter(c => c.type !== 'tovi'));
    } catch { /* silent */ }
  }, []);

  const fetchUnread = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/chat/unread-count`, { headers });
      setTotalUnread(res.data.total_unread);
    } catch { /* silent */ }
  }, []);

  const fetchMessages = useCallback(async (convId) => {
    try {
      const res = await axios.get(`${API}/chat/conversations/${convId}/messages?limit=100`, { headers });
      setMessages(res.data.messages || []);
      setMemberCount(res.data.member_count || 2);
      fetchUnread();
    } catch { toast.error('Failed to load messages'); }
  }, []);

  const fetchTyping = useCallback(async (convId) => {
    try {
      const res = await axios.get(`${API}/chat/conversations/${convId}/typing`, { headers });
      setTypingUsers(res.data || []);
    } catch { /* silent */ }
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/chat/users`, { headers });
      setAllUsers(res.data);
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    if (!token) return;
    fetchUnread();
    const interval = setInterval(fetchUnread, 15000);
    return () => clearInterval(interval);
  }, [token]);

  useEffect(() => {
    if (open) { fetchConversations(); fetchUsers(); fetchTovi(); checkToviNotifications(); }
  }, [open]);

  useEffect(() => {
    if (view === 'chat' && activeConv) {
      fetchMessages(activeConv.id);
      // Skip polling for Tovi - responses come inline
      if (activeConv.type === 'tovi') return;
      fetchTyping(activeConv.id);
      pollRef.current = setInterval(() => fetchMessages(activeConv.id), 5000);
      typingPollRef.current = setInterval(() => fetchTyping(activeConv.id), 2000);
      return () => { clearInterval(pollRef.current); clearInterval(typingPollRef.current); };
    }
    return () => { clearInterval(pollRef.current); clearInterval(typingPollRef.current); };
  }, [view, activeConv?.id]);

  const openChat = (conv) => {
    setActiveConv(conv);
    setView('chat');
    setTypingUsers([]);
    setReplyTo(null);
    setShowReactionPicker(null);
  };

  const sendMessage = async () => {
    if (!msgText.trim() || sending) return;
    setSending(true);
    try {
      // Tovi bot conversation uses dedicated endpoint
      if (activeConv?.type === 'tovi') {
        // Optimistically add user message to UI
        const tempUserMsg = {
          id: 'temp-' + Date.now(),
          conversation_id: activeConv.id,
          sender_id: user.id,
          sender_name: user.name,
          text: msgText,
          read_by: { [user.id]: new Date().toISOString() },
          created_at: new Date().toISOString(),
        };
        setMessages(prev => [...prev, tempUserMsg]);
        const sentText = msgText;
        setMsgText('');
        setToviTyping(true);
        try {
          const res = await axios.post(`${API}/chat/tovi/${activeConv.id}/message`, { text: sentText }, { headers });
          // Replace temp message with real one and add bot response
          setMessages(prev => {
            const filtered = prev.filter(m => m.id !== tempUserMsg.id);
            return [...filtered, res.data.user_message, res.data.bot_response];
          });
          // Update tovi conv last message
          setToviConv(prev => prev ? { ...prev, last_message: res.data.bot_response.text?.slice(0, 100), last_message_at: res.data.bot_response.created_at } : prev);
        } catch {
          toast.error('Tovi failed to respond');
          fetchMessages(activeConv.id);
        } finally {
          setToviTyping(false);
        }
      } else if (replyTo) {
        await axios.post(`${API}/chat/conversations/${activeConv.id}/reply`, { text: msgText, reply_to_id: replyTo.id }, { headers });
        setMsgText('');
        setReplyTo(null);
        fetchMessages(activeConv.id);
        fetchConversations();
      } else {
        await axios.post(`${API}/chat/conversations/${activeConv.id}/messages`, { text: msgText }, { headers });
        setMsgText('');
        setReplyTo(null);
        fetchMessages(activeConv.id);
        fetchConversations();
      }
    } catch { toast.error('Failed to send'); }
    finally { setSending(false); }
  };

  const toggleReaction = async (msgId, emoji) => {
    try {
      const res = await axios.post(`${API}/chat/messages/${msgId}/react`, { emoji }, { headers });
      setMessages(prev => prev.map(m => m.id === msgId ? { ...m, reactions: res.data.reactions } : m));
      setShowReactionPicker(null);
    } catch { toast.error('Failed to react'); }
  };

  const sendTyping = () => {
    if (!activeConv) return;
    axios.post(`${API}/chat/conversations/${activeConv.id}/typing`, {}, { headers }).catch(() => {});
  };

  const handleInputChange = (e) => {
    setMsgText(e.target.value);
    if (activeConv?.type === 'tovi') return; // No typing notification for bot
    sendTyping();
    clearTimeout(typingTimerRef.current);
    typingTimerRef.current = setTimeout(() => {}, 3000);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error('File must be under 10MB'); return; }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await axios.post(`${API}/chat/conversations/${activeConv.id}/upload`, formData, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' }
      });
      fetchMessages(activeConv.id);
      fetchConversations();
      toast.success('File shared');
    } catch { toast.error('File upload failed'); }
    finally { setUploading(false); }
  };

  const startDM = async (otherId) => {
    try {
      const res = await axios.post(`${API}/chat/conversations`, { type: 'dm', members: [otherId] }, { headers });
      setActiveConv(res.data);
      setView('chat');
      fetchConversations();
    } catch { toast.error('Failed to start conversation'); }
  };

  const createGroup = async () => {
    if (!newGroupName.trim()) { toast.error('Group name required'); return; }
    if (newGroupMembers.length === 0) { toast.error('Select at least one member'); return; }
    try {
      const res = await axios.post(`${API}/chat/conversations`, {
        type: newConvType, name: newGroupName, members: newGroupMembers
      }, { headers });
      setActiveConv(res.data);
      setView('chat');
      setNewGroupName(''); setNewGroupMembers([]); setNewConvType('dm');
      fetchConversations();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create'); }
  };

  const toggleMember = (uid) => {
    setNewGroupMembers(prev => prev.includes(uid) ? prev.filter(x => x !== uid) : [...prev, uid]);
  };

  const getConvDisplayName = (conv) => {
    if (conv.type === 'tovi') return 'Tovi';
    if (conv.type === 'dm') {
      const otherId = conv.members.find(m => m !== user.id);
      return conv.member_names?.[otherId] || 'Unknown';
    }
    return conv.name || 'Group Chat';
  };

  if (!token) return null;

  return (
    <>
      <button
        data-testid="chat-widget-toggle"
        onClick={() => setOpen(!open)}
        className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-50 w-12 h-12 sm:w-14 sm:h-14 bg-blue-600 hover:bg-blue-700 text-white rounded-full shadow-lg flex items-center justify-center transition-all hover:scale-105 active:scale-95"
      >
        {open ? <X size={22} /> : <MessageCircle size={22} />}
        {!open && totalUnread > 0 && (
          <span data-testid="chat-unread-badge" className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
            {totalUnread > 9 ? '9+' : totalUnread}
          </span>
        )}
      </button>

      {open && (
        <div data-testid="chat-panel" className="fixed bottom-20 right-2 sm:bottom-24 sm:right-6 z-50 w-[calc(100vw-16px)] sm:w-[380px] h-[70vh] sm:h-[520px] bg-white border border-slate-200 rounded-2xl shadow-2xl flex flex-col overflow-hidden animate-in slide-in-from-bottom-4 duration-200">
          {view === 'list' && (
            <ConversationList
              conversations={conversations}
              toviConv={toviConv}
              userId={user.id}
              onOpenChat={openChat}
              onNewChat={() => setView('new')}
              getConvDisplayName={getConvDisplayName}
            />
          )}

          {view === 'chat' && activeConv && (
            <ChatView
              activeConv={activeConv}
              messages={messages}
              memberCount={memberCount}
              msgText={msgText}
              typingUsers={typingUsers}
              sending={sending}
              uploading={uploading}
              replyTo={replyTo}
              showReactionPicker={showReactionPicker}
              userId={user.id}
              isTovi={activeConv?.type === 'tovi'}
              toviTyping={toviTyping}
              getConvDisplayName={getConvDisplayName}
              onBack={() => { setView('list'); setTypingUsers([]); fetchConversations(); fetchTovi(); }}
              onSendMessage={sendMessage}
              onInputChange={handleInputChange}
              onFileUpload={handleFileUpload}
              onSetReplyTo={setReplyTo}
              onToggleReaction={toggleReaction}
              onSetShowReactionPicker={setShowReactionPicker}
            />
          )}

          {view === 'new' && (
            <NewConversation
              userRole={user.role}
              allUsers={allUsers}
              searchUser={searchUser}
              setSearchUser={setSearchUser}
              newConvType={newConvType}
              setNewConvType={(t) => { setNewConvType(t); setNewGroupMembers([]); }}
              newGroupName={newGroupName}
              setNewGroupName={setNewGroupName}
              newGroupMembers={newGroupMembers}
              onToggleMember={toggleMember}
              onStartDM={startDM}
              onCreateGroup={createGroup}
              onBack={() => { setView('list'); setNewConvType('dm'); setNewGroupName(''); setNewGroupMembers([]); setSearchUser(''); }}
            />
          )}
        </div>
      )}
    </>
  );
}
